(function () {
  'use strict';

  var DEFAULT_ERROR =
    'Something went wrong. Please check the required fields and try again.';
  var PPF_ENROLLMENT_COOKIE = 'ppf_pending_enrollment';

  function isShopifyPixelSandbox() {
    try {
      return /web-pixels@|\/sandbox\/modern\//.test(window.location.href || '');
    } catch (e) {
      return false;
    }
  }

  function readEnrollmentCookie() {
    try {
      var match = document.cookie.match(new RegExp('(?:^|; )' + PPF_ENROLLMENT_COOKIE + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
      return null;
    }
  }

  function writeEnrollmentCookie(id) {
    if (!id) return;
    try {
      document.cookie =
        PPF_ENROLLMENT_COOKIE +
        '=' +
        encodeURIComponent(id) +
        '; path=/; max-age=172800; SameSite=Lax';
    } catch (e) { /* ignore */ }
  }

  function clearEnrollmentCookie() {
    try {
      document.cookie = PPF_ENROLLMENT_COOKIE + '=; path=/; max-age=0; SameSite=Lax';
    } catch (e) { /* ignore */ }
  }

  function readPendingEnrollmentId() {
    var params = new URLSearchParams(window.location.search);
    var fromQuery = params.get('ppf_enrollment') || params.get('enrollment');
    if (fromQuery) return fromQuery;
    var fromCookie = readEnrollmentCookie();
    if (fromCookie) return fromCookie;
    try {
      return window.sessionStorage.getItem(PPF_ENROLLMENT_COOKIE);
    } catch (e) {
      return null;
    }
  }

  function persistPendingEnrollmentId(id) {
    writeEnrollmentCookie(id);
    try {
      window.sessionStorage.setItem(PPF_ENROLLMENT_COOKIE, id);
    } catch (e) { /* ignore — cookie fallback above */ }
  }

  function clearPendingEnrollmentId() {
    clearEnrollmentCookie();
    try {
      window.sessionStorage.removeItem(PPF_ENROLLMENT_COOKIE);
    } catch (e) { /* ignore */ }
  }

  function showFormError(state, message) {
    if (state.alertEl) {
      state.alertEl.hidden = false;
      var msgEl = state.alertEl.querySelector('[data-form-alert-message]');
      if (msgEl) msgEl.textContent = message || DEFAULT_ERROR;
    }
  }

  function checkoutErrorMessage(err) {
    if (!err) return DEFAULT_ERROR;
    if (err.status === 404) {
      return 'Payment checkout is not available yet. The enrollment API needs to be deployed — contact APEX support if this continues.';
    }
    if (err.status === 503 && err.data && err.data.code === 'CHECKOUT_FAILED') {
      return err.message || 'Payment checkout is not configured yet. Please try again later or contact APEX support.';
    }
    if (err.message && err.message.includes('SHOPIFY_STOREFRONT_TOKEN')) {
      return 'Monthly subscription checkout is not configured yet. Ask APEX to add the Storefront API token on the enrollment server.';
    }
    if (err.message) return err.message;
    return DEFAULT_ERROR;
  }

  var REQUIRED = [
    'planDuration',
    'paymentPreference',
    'firstName',
    'lastName',
    'email',
    'phone',
    'vehicleYear',
    'vehicleMake',
    'vehicleModel',
    'installationDate',
    'installingDealer',
    'orderNumber',
    'installationAreas',
    'termsAgreed',
    'planTermsAgreed'
  ];

  var RECEIPT_MAX_BYTES = 10 * 1024 * 1024;
  var RECEIPT_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp'
  ];

  function fileToBase64(file) {
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onload = function () {
        resolve(reader.result || '');
      };
      reader.onerror = function () {
        reject(new Error('Could not read file'));
      };
      reader.readAsDataURL(file);
    });
  }

  function getReceiptInput(form) {
    return form.querySelector('[data-ppf-receipt]');
  }

  function getReceiptFile(form) {
    var input = getReceiptInput(form);
    if (!input || !input.files || !input.files[0]) return null;
    return input.files[0];
  }

  function isAllowedReceiptType(file) {
    if (!file) return false;
    var type = (file.type || '').toLowerCase();
    if (RECEIPT_TYPES.indexOf(type) !== -1) return true;
    var name = (file.name || '').toLowerCase();
    return /\.(pdf|jpe?g|png|webp)$/.test(name);
  }

  function updateReceiptFileName(form) {
    var input = getReceiptInput(form);
    var label = form.querySelector('[data-receipt-file-name]');
    var field = form.querySelector('.ppf-plan__file-field');
    var file = getReceiptFile(form);
    if (!label) return;
    if (!file) {
      label.textContent = 'No file chosen';
      if (field) field.classList.remove('has-file');
      return;
    }
    label.textContent = file.name;
    if (field) field.classList.add('has-file');
  }

  function collectData(form) {
    var data = {};
    Array.prototype.forEach.call(form.querySelectorAll('[data-field]'), function (el) {
      var key = el.getAttribute('data-field');
      if (!key) return;
      if (el.type === 'checkbox') {
        data[key] = !!el.checked;
      } else if (el.type === 'radio') {
        if (el.checked) data[key] = (el.value || '').trim();
      } else {
        data[key] = (el.value || '').trim();
      }
    });
    if (data.email) data.email = data.email.toLowerCase();
    return data;
  }

  function markCheckboxInvalid(form, key, invalid) {
    Array.prototype.forEach.call(form.querySelectorAll('[data-field="' + key + '"]'), function (el) {
      var termsWrap = el.closest('.dealer-app__terms');
      if (termsWrap) termsWrap.classList.toggle('is-invalid', invalid);
    });
  }

  function validateForm(form, data) {
    var invalid = [];
    REQUIRED.forEach(function (key) {
      var v = data[key];
      if (key === 'termsAgreed' || key === 'planTermsAgreed') {
        if (!v) invalid.push(key);
      } else if (!v) {
        invalid.push(key);
      }
    });
    if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      invalid.push('email');
    }

    var receiptFile = getReceiptFile(form);
    var receiptField = form.querySelector('.ppf-plan__file-field');
    if (!receiptFile) {
      invalid.push('receiptFile');
      if (receiptField) receiptField.classList.add('is-invalid');
    } else if (!isAllowedReceiptType(receiptFile)) {
      invalid.push('receiptFileType');
      if (receiptField) receiptField.classList.add('is-invalid');
    } else if (receiptFile.size > RECEIPT_MAX_BYTES) {
      invalid.push('receiptFileSize');
      if (receiptField) receiptField.classList.add('is-invalid');
    } else if (receiptField) {
      receiptField.classList.remove('is-invalid');
    }

    Array.prototype.forEach.call(form.querySelectorAll('[data-field]'), function (el) {
      var key = el.getAttribute('data-field');
      if (key === 'termsAgreed' || key === 'planTermsAgreed') {
        markCheckboxInvalid(form, key, invalid.indexOf(key) !== -1);
        return;
      }
      var wrapper = el.closest('.field');
      if (!wrapper || el.type === 'radio') return;
      wrapper.classList.toggle('is-invalid', invalid.indexOf(key) !== -1);
    });

    return invalid;
  }

  function bindFieldListeners(form) {
    Array.prototype.forEach.call(form.querySelectorAll('[data-field]'), function (field) {
      if (field.getAttribute('data-ppf-bound') === '1') return;
      field.setAttribute('data-ppf-bound', '1');
      field.addEventListener('input', function () {
        var wrapper = field.closest('.field');
        if (wrapper) wrapper.classList.remove('is-invalid');
      });
      field.addEventListener('change', function () {
        var wrapper = field.closest('.field');
        if (wrapper) wrapper.classList.remove('is-invalid');
        var key = field.getAttribute('data-field');
        if (field.type === 'checkbox' && (key === 'termsAgreed' || key === 'planTermsAgreed') && field.checked) {
          markCheckboxInvalid(form, key, false);
        }
      });
    });

    var receiptInput = getReceiptInput(form);
    if (receiptInput && receiptInput.getAttribute('data-ppf-receipt-bound') !== '1') {
      receiptInput.setAttribute('data-ppf-receipt-bound', '1');
      receiptInput.addEventListener('change', function () {
        var receiptField = form.querySelector('.ppf-plan__file-field');
        if (receiptField) receiptField.classList.remove('is-invalid');
        updateReceiptFileName(form);
      });
    }
  }

  function bindTermsModal(root) {
    if (!root || root.getAttribute('data-ppf-terms-bound') === '1') return;
    root.setAttribute('data-ppf-terms-bound', '1');

    var openBtn = root.querySelector('[data-ppf-terms-open]');
    if (!openBtn || openBtn.getAttribute('data-ppf-terms-open-bound') === '1') return;
    openBtn.setAttribute('data-ppf-terms-open-bound', '1');

    openBtn.addEventListener('click', function (event) {
      event.preventDefault();
      var modalId = openBtn.getAttribute('aria-controls');
      if (!modalId) return;
      var modal = document.getElementById(modalId);
      if (!modal || typeof modal.show !== 'function') return;
      modal.show();
      openBtn.setAttribute('aria-expanded', 'true');
    });
  }

  function getRoot(form) {
    return form.closest('ppf-plan-form') || form.closest('.dealer-app') || form.parentElement;
  }

  function submitEnrollment(form, root, state) {
    if (state.submitting) return;
    var data = collectData(form);
    var invalid = validateForm(form, data);
    if (invalid.length) {
      if (state.alertEl) state.alertEl.hidden = false;
      var first =
        form.querySelector('.field.is-invalid [data-field]') ||
        form.querySelector('.ppf-plan__file-field.is-invalid [data-ppf-receipt]') ||
        form.querySelector('.dealer-app__terms.is-invalid [data-field]');
      if (first && typeof first.focus === 'function') first.focus();
      return;
    }
    if (state.alertEl) state.alertEl.hidden = true;

    var receiptFile = getReceiptFile(form);
    if (!receiptFile) {
      if (state.alertEl) state.alertEl.hidden = false;
      return;
    }

    state.submitting = true;
    if (state.submitBtn) {
      state.submitBtn.disabled = true;
      state.submitBtn.classList.add('is-submitting');
      var btnText = state.submitBtn.querySelector('.btn-text');
      if (btnText && !btnText.dataset.original) {
        btnText.dataset.original = btnText.textContent;
        btnText.textContent = 'Redirecting to payment…';
      }
    }

    var api = window.ApexFormApi;

    function resetSubmitBtn() {
      state.submitting = false;
      if (state.submitBtn) {
        state.submitBtn.disabled = false;
        state.submitBtn.classList.remove('is-submitting');
        var t = state.submitBtn.querySelector('.btn-text');
        if (t && t.dataset.original) {
          t.textContent = t.dataset.original;
          delete t.dataset.original;
        }
      }
    }

    function showSuccessView() {
      if (state.formView) state.formView.hidden = true;
      if (state.successView) {
        state.successView.hidden = false;
        if (typeof state.successView.scrollIntoView === 'function') {
          state.successView.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }

    function fail(err) {
      resetSubmitBtn();
      showFormError(state, checkoutErrorMessage(err));
    }

    if (!api || typeof api.startPpfCheckout !== 'function') {
      fail({
        message:
          'Enrollment checkout is unavailable. Please refresh the page. If this continues, the site scripts may need to be updated.'
      });
      return;
    }

    fileToBase64(receiptFile)
      .then(function (base64) {
        return api.startPpfCheckout({
          form: 'ppf-plan',
          page: window.location.pathname,
          source: 'shopify-theme',
          data: data,
          attachments: {
            receipt: {
              name: receiptFile.name,
              contentType: receiptFile.type || 'application/octet-stream',
              content: base64
            }
          }
        });
      })
      .then(function (res) {
        if (!res || !res.checkoutUrl || !res.id) {
          throw new Error('Could not start checkout. No payment URL was returned.');
        }
        persistPendingEnrollmentId(res.id);
        window.location.assign(res.checkoutUrl);
      })
      .catch(fail);
  }

  function confirmPendingEnrollment(root, state) {
    var api = window.ApexFormApi;
    if (!api || typeof api.confirmPpfEnrollment !== 'function') return;

    var enrollmentId = readPendingEnrollmentId();
    if (!enrollmentId) return;

    var params = new URLSearchParams(window.location.search);
    var orderId = params.get('order_id') || params.get('orderId') || null;

    if (state.confirming) return;
    state.confirming = true;

    api
      .confirmPpfEnrollment(enrollmentId, orderId)
      .then(function (res) {
        state.confirming = false;
        if (!res || !res.ok) return;
        clearPendingEnrollmentId();
        if (state.referenceEl && res.id) {
          state.referenceEl.textContent = 'Reference: ' + res.id;
        }
        if (state.formView) state.formView.hidden = true;
        if (state.successView) {
          state.successView.hidden = false;
          if (typeof state.successView.scrollIntoView === 'function') {
            state.successView.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
        if (window.history && window.history.replaceState) {
          var cleanUrl = window.location.pathname + window.location.hash;
          window.history.replaceState({}, document.title, cleanUrl);
        }
      })
      .catch(function () {
        state.confirming = false;
      });
  }

  function bindPpfPlanForm(form) {
    if (!form || form.getAttribute('data-ppf-form-bound') === '1') return;
    form.setAttribute('data-ppf-form-bound', '1');
    form.setAttribute('novalidate', 'novalidate');

    var root = getRoot(form);
    var sectionRoot = form.closest('.section-ppf-plan') || form.closest('.premier-app') || document;
    bindTermsModal(sectionRoot);

    var state = {
      submitting: false,
      confirming: false,
      formView: root ? root.querySelector('[data-view="form"]') : null,
      successView: root ? root.querySelector('[data-view="success"]') : null,
      alertEl: form.querySelector('[data-form-alert]') || (root ? root.querySelector('[data-form-alert]') : null),
      submitBtn: form.querySelector('[data-form-submit]') || (root ? root.querySelector('[data-form-submit]') : null),
      referenceEl: root ? root.querySelector('[data-success-reference]') : null
    };

    bindFieldListeners(form);
    confirmPendingEnrollment(root, state);

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      event.stopPropagation();
      submitEnrollment(form, root, state);
    });

    if (state.submitBtn && state.submitBtn.getAttribute('data-ppf-click-bound') !== '1') {
      state.submitBtn.setAttribute('data-ppf-click-bound', '1');
      state.submitBtn.setAttribute('type', 'button');
      state.submitBtn.addEventListener('click', function (event) {
        event.preventDefault();
        submitEnrollment(form, root, state);
      });
    }

    var submitAnotherBtn = root ? root.querySelector('[data-submit-another]') : null;
    if (submitAnotherBtn && submitAnotherBtn.getAttribute('data-ppf-bound') !== '1') {
      submitAnotherBtn.setAttribute('data-ppf-bound', '1');
      submitAnotherBtn.addEventListener('click', function () {
        if (state.successView) state.successView.hidden = true;
        if (state.formView) state.formView.hidden = false;
        state.submitting = false;
        form.reset();
        updateReceiptFileName(form);
        if (state.submitBtn) {
          state.submitBtn.disabled = false;
          state.submitBtn.classList.remove('is-submitting');
        }
        var anchor = document.getElementById('ppf-plan-enroll');
        if (anchor && typeof anchor.scrollIntoView === 'function') {
          anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  }

  function initPpfPlanForms() {
    if (isShopifyPixelSandbox()) return;
    document.querySelectorAll('[data-ppf-plan-form]').forEach(bindPpfPlanForm);
  }

  window.initPpfPlanForms = initPpfPlanForms;

  if (!(window.customElements && window.customElements.get('ppf-plan-form'))) {
    class PpfPlanForm extends HTMLElement {
      connectedCallback() {
        var form = this.querySelector('[data-ppf-plan-form]');
        if (form) bindPpfPlanForm(form);
      }
    }
    window.customElements.define('ppf-plan-form', PpfPlanForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPpfPlanForms);
  } else {
    initPpfPlanForms();
  }

  document.addEventListener('shopify:section:load', initPpfPlanForms);

  document.addEventListener(
    'submit',
    function (event) {
      if (isShopifyPixelSandbox()) return;
      var form = event.target;
      if (form && form.hasAttribute && form.hasAttribute('data-ppf-plan-form')) {
        event.preventDefault();
        event.stopPropagation();
        if (form.getAttribute('data-ppf-form-bound') !== '1') {
          bindPpfPlanForm(form);
        }
      }
    },
    true
  );
})();
